/**
 * Trace shape geometry — Bézier path building + auto-handle classification.
 *
 * A Trace layer holds one closed shape as structured nodes (see TraceNode); the
 * rendered SVG `d` is *derived* from those nodes here, so the shape stays
 * editable. On close each node is classified (brief §5.2) into one of four
 * handle types and given handles to match — the user can later flip a node
 * between smooth and cusp, but cannot hand-author every type.
 */
import { apply, transformToMatrix, isIdentityTransform, type Mat } from '@/geometry/affine';
import type { SymmetryVariant } from '@/patterns/types';
import type { HandleType, TraceNode } from '@/types/drawing';

type Pt = { x: number; y: number };

/**
 * How well a candidate link's *neighbourhood* corresponds: after applying the
 * link transform to the source node's two neighbours, how far do they land from
 * the target node's two neighbours? Takes the better of the aligned and flipped
 * pairings (a reflection reverses edge order). Small ⇒ a genuine symmetry seam
 * (the edges continue across the join); large ⇒ the two points merely coincide
 * in space (e.g. a coincidental lattice-translate) and must NOT be linked.
 */
export function linkNeighbourError(m: Mat, sPrev: Pt, sNext: Pt, nPrev: Pt, nNext: Pt): number {
  const tp = apply(m, [sPrev.x, sPrev.y]);
  const tq = apply(m, [sNext.x, sNext.y]);
  const d = (a: [number, number], b: Pt): number => Math.hypot(a[0] - b.x, a[1] - b.y);
  const aligned = d(tp, nPrev) + d(tq, nNext);
  const flipped = d(tp, nNext) + d(tq, nPrev);
  return Math.min(aligned, flipped);
}

/**
 * Per-edge link correspondence — the per-handle successor to linkNeighbourError.
 *
 * Reuses the same aligned-vs-flipped evidence, but *per edge* instead of summed,
 * and keeps which source slot won instead of discarding it. Transform the
 * source's two neighbours through `m`; for the target's in-edge (the edge
 * arriving from nPrev) compare the transformed source-prev against it (source's
 * in-handle drives, no swap) vs the transformed source-next (source's out-handle
 * drives, swap) — the smaller distance decides the slot and reports its error.
 * Symmetric for the out-edge (leaving toward nNext).
 *
 * This is exactly what `linkHandlesSwap` decides, resolved independently for each
 * of the target's two edges so a corner node can link each handle to a different
 * source under a different operation (brief §2 — the corner fix).
 */
export function edgeLinkCorrespondence(
  m: Mat,
  sPrev: Pt,
  sNext: Pt,
  nPrev: Pt,
  nNext: Pt,
): {
  in: { slot: 'in' | 'out'; err: number };
  out: { slot: 'in' | 'out'; err: number };
} {
  const tp = apply(m, [sPrev.x, sPrev.y]); // image of source's prev-neighbour (source in-edge)
  const tq = apply(m, [sNext.x, sNext.y]); // image of source's next-neighbour (source out-edge)
  const d = (a: [number, number], b: Pt): number => Math.hypot(a[0] - b.x, a[1] - b.y);
  // N's in-edge arrives from nPrev: does the source's in-edge (tp) or out-edge (tq) land on it?
  const inFromIn = d(tp, nPrev);
  const inFromOut = d(tq, nPrev);
  const inEdge = inFromIn <= inFromOut ? { slot: 'in' as const, err: inFromIn } : { slot: 'out' as const, err: inFromOut };
  // N's out-edge leaves toward nNext.
  const outFromOut = d(tq, nNext);
  const outFromIn = d(tp, nNext);
  const outEdge = outFromOut <= outFromIn ? { slot: 'out' as const, err: outFromOut } : { slot: 'in' as const, err: outFromIn };
  return { in: inEdge, out: outEdge };
}

// ---------------------------------------------------------------------------
// Bug 1 — shared-edge Bézier constraint for *self-fixed* nodes.
//
// inkSnap.ts's computeSnap links two *distinct* nodes that are symmetry
// images of each other. A node sitting exactly on a shared edge's own
// self-gluing point (e.g. the midpoint of an edge a 180° rotation, or a
// mirror perpendicular to it, maps onto itself) can never get a link that
// way — under the fixing op it maps to itself, which computeSnap treats as
// "coincident, skip" by design (that's what stops a closed path's own
// start/end node from linking to itself). Such a node still needs its curve
// constrained, just against itself rather than against a partner node.
// ---------------------------------------------------------------------------

/** node position must land within this of the op's fixed point (tile units) —
 *  looser than computeSnap's 1e-6 "coincident" check, which compares two
 *  already-exact transform outputs; this compares a real hand-placed node. */
const SELF_FIX_POS_TOL = 0.015;
/** neighbours must correspond too, for a genuine edge-gluing (not a
 *  coincidentally-fixed lattice point) — matches useTraceEditor's LINK_NEIGHBOUR_TOL. */
const SELF_FIX_NEIGHBOUR_TOL = 0.06;
/** handle-vector match tolerance when deciding if an as-drawn curve already satisfies the constraint. */
const SELF_FIX_HANDLE_TOL = 1e-3;

function applyLinear(m: Mat, hx: number, hy: number): [number, number] {
  return [m[0] * hx + m[2] * hy, m[1] * hx + m[3] * hy];
}

export interface SelfFixingOp {
  /** the fixing symmetry's matrix (linear part is what constrains the handles) */
  matrix: Mat;
  /** true ⇒ the op maps prev↔next (a genuine edge-midpoint gluing — the
   *  common case); false ⇒ it fixes prev and next in place (a mirror axis
   *  running along the path itself through this node — rare). */
  swap: boolean;
}

/**
 * Does `node` sit at a fixed point of some non-identity symmetry op that also
 * glues its two path-neighbours onto each other, i.e. a genuine shared-edge
 * self-gluing rather than a coincidentally-fixed lattice point? Mirrors
 * resolveLinks'/computeSnap's "position matches AND neighbourhood matches"
 * two-part test (useTraceEditor.ts), just against the node's own neighbours
 * instead of another node's.
 */
export function findSelfFixingOp(
  variant: SymmetryVariant,
  node: Pt,
  prev: Pt,
  next: Pt,
  rings = 2,
): SelfFixingOp | null {
  let best: SelfFixingOp | null = null;
  let bestErr = SELF_FIX_NEIGHBOUR_TOL;
  for (const t of variant.transformsInRange(rings)) {
    if (isIdentityTransform(t)) continue;
    const m = transformToMatrix(t);
    const [mx, my] = apply(m, [node.x, node.y]);
    if (Math.hypot(mx - node.x, my - node.y) > SELF_FIX_POS_TOL) continue;
    const err = linkNeighbourError(m, prev, next, prev, next);
    if (err < bestErr) {
      bestErr = err;
      const [px, py] = apply(m, [prev.x, prev.y]);
      const [qx, qy] = apply(m, [next.x, next.y]);
      const aligned = Math.hypot(px - prev.x, py - prev.y) + Math.hypot(qx - next.x, qy - next.y);
      const flipped = Math.hypot(px - next.x, py - next.y) + Math.hypot(qx - prev.x, qy - prev.y);
      best = { matrix: m, swap: flipped < aligned };
    }
  }
  return best;
}

/**
 * Does a node's own (independently-derived) handle pair already satisfy a
 * self-fixing op's constraint — anti-symmetric about the shared edge, per
 * Bug 1's fix algorithm? If the op swaps prev/next, the transformed out-handle
 * must equal the in-handle (and vice versa); if it fixes them in place, each
 * handle must map to itself. Anything else falls back to a straight edge
 * (zero handles), which trivially satisfies any linear constraint — the
 * transform of zero is always zero.
 */
export function constrainSelfFixedHandles(
  h: { inX: number; inY: number; outX: number; outY: number },
  op: SelfFixingOp,
): { inX: number; inY: number; outX: number; outY: number } {
  const [tOutX, tOutY] = applyLinear(op.matrix, h.outX, h.outY);
  const [tInX, tInY] = applyLinear(op.matrix, h.inX, h.inY);
  const close = (ax: number, ay: number, bx: number, by: number): boolean =>
    Math.hypot(ax - bx, ay - by) < SELF_FIX_HANDLE_TOL;
  const valid = op.swap
    ? close(tOutX, tOutY, h.inX, h.inY) && close(tInX, tInY, h.outX, h.outY)
    : close(tOutX, tOutY, h.outX, h.outY) && close(tInX, tInY, h.inX, h.inY);
  return valid ? h : { inX: 0, inY: 0, outX: 0, outY: 0 };
}

/** out-handle length as a fraction of the adjacent edge (Catmull-Rom-ish). */
const K = 1 / 3;

// Handle-type bands by interior angle at the node. `cos` below is the dot of the
// incoming/outgoing edge *directions* = cos(turn angle) = −cos(interior angle),
// so a larger value means a gentler (straighter) node. Borderline angles lean to
// the smoother type. 90° is kept as a corner; ~hexagon angles (120°) smooth out.
const cosForInterior = (deg: number): number => -Math.cos((deg * Math.PI) / 180);
/** ≥150° → smooth */
const SMOOTH_COS = cosForInterior(150); // ≈ 0.866
/** 110°–150° → smooth asymmetrical (gentle) */
const GENTLE_COS = cosForInterior(110); // ≈ 0.342
/** 30°–110° → one-sided / cusp (sharp); below 30° → cusp (corner) */
const SHARP_COS = cosForInterior(30); // ≈ −0.866
/** edge-length ratio below which a smooth node counts as asymmetric. */
const ASYM_RATIO = 0.6;
/** edge-length ratio below which a corner has "one very short edge". */
const ONE_SIDED_RATIO = 0.2;

function f(n: number): string {
  return Number(n.toFixed(4)).toString();
}

/**
 * Build the SVG path for a node list. Cubic segment i→i+1 uses node i's
 * out-handle as the first control point and node i+1's in-handle as the second:
 *   C (xi+outi) (yi+outi)  (xj+inj) (yj+inj)  xj yj
 */
export function buildTraceD(nodes: readonly TraceNode[], closed: boolean): string {
  if (nodes.length === 0) return '';
  const a0 = nodes[0]!;
  let d = `M ${f(a0.x)} ${f(a0.y)}`;
  for (let i = 1; i < nodes.length; i++) {
    const p = nodes[i - 1]!;
    const c = nodes[i]!;
    d += ` C ${f(p.x + p.outX)} ${f(p.y + p.outY)} ${f(c.x + c.inX)} ${f(c.y + c.inY)} ${f(c.x)} ${f(c.y)}`;
  }
  if (closed && nodes.length > 2) {
    const last = nodes[nodes.length - 1]!;
    d += ` C ${f(last.x + last.outX)} ${f(last.y + last.outY)} ${f(a0.x + a0.inX)} ${f(a0.y + a0.inY)} ${f(a0.x)} ${f(a0.y)} Z`;
  }
  return d;
}

function len(dx: number, dy: number): number {
  return Math.hypot(dx, dy);
}

/**
 * Classify a node from its neighbours' geometry (brief §5.2).
 * `prev`/`next` are the adjacent node positions.
 */
export function classifyNode(
  prev: { x: number; y: number },
  node: { x: number; y: number },
  next: { x: number; y: number },
): HandleType {
  const inDX = node.x - prev.x;
  const inDY = node.y - prev.y;
  const outDX = next.x - node.x;
  const outDY = next.y - node.y;
  const lin = len(inDX, inDY);
  const lout = len(outDX, outDY);
  if (lin < 1e-9 || lout < 1e-9) return 'cusp';

  // angle between incoming and outgoing edge directions; cos≈1 ⇒ near-straight.
  const cos = (inDX * outDX + inDY * outDY) / (lin * lout);
  const ratio = Math.min(lin, lout) / Math.max(lin, lout);

  if (cos >= SMOOTH_COS) return ratio >= ASYM_RATIO ? 'smooth-sym' : 'smooth-asym'; // ≥150°
  if (cos >= GENTLE_COS) return 'smooth-asym'; // 110°–150°
  if (cos >= SHARP_COS) return ratio < ONE_SIDED_RATIO ? 'one-sided' : 'cusp'; // 30°–110°
  return 'cusp'; // <30° corner
}

/**
 * Handle vectors for a node of a given type, from its neighbours.
 * Pure: callers assign the result onto the node and rebuild `d`.
 */
export function deriveHandles(
  prev: { x: number; y: number },
  node: { x: number; y: number },
  next: { x: number; y: number },
  type: HandleType,
): { inX: number; inY: number; outX: number; outY: number } {
  const inDX = node.x - prev.x;
  const inDY = node.y - prev.y;
  const outDX = next.x - node.x;
  const outDY = next.y - node.y;
  const lin = len(inDX, inDY) || 1e-9;
  const lout = len(outDX, outDY) || 1e-9;

  switch (type) {
    case 'smooth-sym':
    case 'smooth-asym': {
      // Tangent through the node, parallel to (next − prev).
      const tx = next.x - prev.x;
      const ty = next.y - prev.y;
      const tl = len(tx, ty) || 1e-9;
      const ux = tx / tl;
      const uy = ty / tl;
      // sym ⇒ equal length both sides; asym ⇒ scale to each edge.
      const outLen = type === 'smooth-sym' ? K * Math.min(lin, lout) : K * lout;
      const inLen = type === 'smooth-sym' ? K * Math.min(lin, lout) : K * lin;
      return { outX: ux * outLen, outY: uy * outLen, inX: -ux * inLen, inY: -uy * inLen };
    }
    case 'one-sided': {
      // Short edge → flat (no handle); long edge → tangent along its own edge.
      if (lin <= lout) {
        return { inX: 0, inY: 0, outX: (outDX / lout) * K * lout, outY: (outDY / lout) * K * lout };
      }
      return { outX: 0, outY: 0, inX: -(inDX / lin) * K * lin, inY: -(inDY / lin) * K * lin };
    }
    case 'cusp':
    default: {
      // Handles run along each edge independently — a corner with curved sides.
      return {
        inX: -(inDX / lin) * K * lin,
        inY: -(inDY / lin) * K * lin,
        outX: (outDX / lout) * K * lout,
        outY: (outDY / lout) * K * lout,
      };
    }
  }
}

/**
 * Classify + assign handles for every not-fully-driven node of a closed shape.
 * A node with *both* handles linked is left untouched — both handles are driven
 * by their sources. A node with only one link is still classified so its free
 * handle gets a sensible default; the close-time propagation pass afterwards
 * overwrites its linked handle, so touching it here is harmless. Mutates a
 * shallow copy and returns it.
 */
export function autoClassify(nodes: readonly TraceNode[], closed: boolean, variant?: SymmetryVariant): TraceNode[] {
  const n = nodes.length;
  return nodes.map((node, i) => {
    if ((node.inLink && node.outLink) || n < 3) return { ...node };
    const prev = closed ? nodes[(i - 1 + n) % n]! : nodes[Math.max(0, i - 1)]!;
    const next = closed ? nodes[(i + 1) % n]! : nodes[Math.min(n - 1, i + 1)]!;
    const type = classifyNode(prev, node, next);
    let h = deriveHandles(prev, node, next, type);
    // Bug 1: a fully-unlinked node is exactly the case that can sit on a
    // self-fixed shared-edge point (see findSelfFixingOp above) — constrain it
    // the same way resolveLinks would if it could produce a link for a fixed
    // point. A node already governed by a cross-link is by definition not a
    // free self-fixed node, so skip the check when either handle is linked.
    if (variant && closed && !node.inLink && !node.outLink) {
      const op = findSelfFixingOp(variant, node, prev, next);
      if (op) h = constrainSelfFixedHandles(h, op);
    }
    return { ...node, type, ...h };
  });
}

/** Cycle a node's handle type for the Flip FAB: smooth ↔ cusp (brief §5.3). */
export function flipHandleType(type: HandleType): HandleType {
  return type === 'cusp' || type === 'one-sided' ? 'smooth-sym' : 'cusp';
}

// ---------------------------------------------------------------------------
// Drawn-edge graph walk (brief 08a) — replaces the old lattice-ray model.
//
// The valid paths from an active node are NOT straight lattice lines: they are
// walks along the graph formed by every already-drawn trace edge AND its
// images under the wallpaper group's symmetry operations. A node placed by
// snapping landed on the image of some other trace's node; the images of that
// node's incident edges are therefore incident to the active position too, and
// each gives a walkable direction.
// ---------------------------------------------------------------------------

export interface TracePathInput {
  nodes: { x: number; y: number }[];
  closed: boolean;
}

export interface TracePath {
  /** ordered node positions along the path, starting at the first step AWAY
   *  from the active node, ending inclusively at the terminus (junction or
   *  polyline endpoint). */
  points: [number, number][];
}

/**
 * Paths walkable from `active` along the graph of drawn edges and their
 * symmetry images (brief 08a). Builds the image-edge graph, canonicalises
 * coincident vertices (so images of different edges that land on the same
 * point merge into one graph vertex), then walks outward from `active`'s
 * vertex along every incident direction except those excluded, stopping
 * inclusively at the first junction (degree ≥ 3, counting the edge just
 * arrived on) or dead end (degree 0 excluding the edge arrived on), or when
 * the walk returns to `active` itself (closed-loop cycle).
 *
 * Pure geometry, no DOM.
 */
export function tracePathsFrom(
  active: Pt,
  traces: readonly TracePathInput[],
  transforms: readonly Mat[],
  tol: number,
  exclude: readonly Pt[] = [],
  maxLen = 200,
): TracePath[] {
  // --- vertex canonicalisation via a spatial hash (3×3 neighbouring-cell probe) ---
  const q = Math.max(tol, 1e-9);
  const cellOf = (x: number, y: number): [number, number] => [Math.round(x / q), Math.round(y / q)];
  const buckets = new Map<string, number[]>(); // cell key -> canonical vertex ids in that cell
  const positions: [number, number][] = []; // canonical vertex id -> representative position

  const findOrCreateVertex = (x: number, y: number): number => {
    const [cx, cy] = cellOf(x, y);
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        const key = `${cx + dx},${cy + dy}`;
        const ids = buckets.get(key);
        if (!ids) continue;
        for (const id of ids) {
          const [px, py] = positions[id]!;
          if (Math.hypot(px - x, py - y) <= tol) return id;
        }
      }
    }
    const id = positions.length;
    positions.push([x, y]);
    const key = `${cx},${cy}`;
    const ids = buckets.get(key);
    if (ids) ids.push(id);
    else buckets.set(key, [id]);
    return id;
  };

  // --- build the deduped image-edge adjacency ---
  const adjacency = new Map<number, Set<number>>();
  const edgeKeys = new Set<string>();
  const addEdge = (a: number, b: number): void => {
    if (a === b) return;
    const key = a < b ? `${a}:${b}` : `${b}:${a}`;
    if (edgeKeys.has(key)) return;
    edgeKeys.add(key);
    (adjacency.get(a) ?? adjacency.set(a, new Set()).get(a)!).add(b);
    (adjacency.get(b) ?? adjacency.set(b, new Set()).get(b)!).add(a);
  };

  for (const trace of traces) {
    const n = trace.nodes.length;
    if (n < 2) continue;
    const edgeCount = trace.closed ? n : n - 1;
    for (let i = 0; i < edgeCount; i++) {
      const a = trace.nodes[i]!;
      const b = trace.nodes[(i + 1) % n]!;
      for (const m of transforms) {
        const [ax, ay] = apply(m, [a.x, a.y]);
        const [bx, by] = apply(m, [b.x, b.y]);
        if (Math.hypot(bx - ax, by - ay) <= tol) continue; // degenerate
        addEdge(findOrCreateVertex(ax, ay), findOrCreateVertex(bx, by));
      }
    }
  }

  // --- locate the active vertex ---
  const [actCx, actCy] = cellOf(active.x, active.y);
  let activeVertex = -1;
  for (let dx = -1; dx <= 1 && activeVertex < 0; dx++) {
    for (let dy = -1; dy <= 1 && activeVertex < 0; dy++) {
      const ids = buckets.get(`${actCx + dx},${actCy + dy}`);
      if (!ids) continue;
      for (const id of ids) {
        const [px, py] = positions[id]!;
        if (Math.hypot(px - active.x, py - active.y) <= tol) {
          activeVertex = id;
          break;
        }
      }
    }
  }
  if (activeVertex < 0) return [];

  const isExcluded = (id: number): boolean => {
    const [px, py] = positions[id]!;
    return exclude.some((p) => Math.hypot(p.x - px, p.y - py) <= tol);
  };

  const starts = [...(adjacency.get(activeVertex) ?? [])].filter((id) => !isExcluded(id));
  const paths: TracePath[] = [];
  const usedStarts = new Set<number>();
  for (const start of starts) {
    if (usedStarts.has(start)) continue; // dedupe identical first-step directions
    usedStarts.add(start);
    const points: [number, number][] = [];
    let prev = activeVertex;
    let cur = start;
    while (true) {
      // A step back onto the active vertex closes a cycle — stop WITHOUT
      // re-appending it; the previously-appended vertex is the (exclusive of
      // active, inclusive of itself) terminus.
      if (cur === activeVertex) break;
      points.push(positions[cur]!);
      if (points.length >= maxLen) break;
      const neighbours = adjacency.get(cur) ?? new Set<number>();
      const next = [...neighbours].filter((id) => id !== prev);
      if (next.length !== 1) break; // dead end (0) or junction (>=2) — inclusive terminus
      prev = cur;
      cur = next[0]!;
    }
    paths.push({ points });
  }
  return paths;
}

/**
 * Snap anchors at the unit-cell corners / tile-boundary special points (08a §4):
 * the vertices of the fundamental-domain polygon.
 *
 * These vertices ARE the grid crossings (cell corners) and rotation centres that
 * bound the drawn tile — the meaningful points to snap to when tracing a
 * tessellation. The cell is centred in [0,1]² with a margin (period ≈0.9, so
 * (0,0) is NOT a corner), which is exactly why deriving corners from the lattice
 * basis at the origin was wrong; the polygon carries the true positions. The
 * caller (snapTargets / snapMarkers) orbit-expands these across the viewport, so
 * one representative per distinct vertex is enough — no per-tile proliferation.
 * De-duplicated for exact coincidences.
 */
export function cellCornerAnchors(polygon: readonly [number, number][]): Pt[] {
  const EPS = 1e-4;
  const out: Pt[] = [];
  for (const [x, y] of polygon) {
    if (!out.some((p) => Math.hypot(p.x - x, p.y - y) < EPS)) out.push({ x, y });
  }
  return out;
}
